import styled, { css } from 'styled-components';
import is from 'styled-is';
import React from 'react';
import { observer } from 'mobx-react';
import colors from '@honeybook/hbui/dist/styles/colors';
import { flexCenterAll } from '@honeybook/hbui/dist/styles/mixins';
import { absoluteAll } from '@honeybook/hbui/dist/styles/mixins';
import FileSparks16 from '@honeybook/icons/dist/icons/FileSparks16';
import FileSparks42 from '@honeybook/icons/dist/icons/FileSparks42';
import { IFlowTemplate, IFlowThumbnail } from '~/types/model.types';
import { getOptimizedThumbnailImage } from '~/utils/image.utils';
import { PendingChangesDotForFlowThumbnail } from '~/components/common/FlowTemplates/FlowPendingChanges/FlowPendingChanges.styles';
import { THUMBNAIL_CROP_RATIO } from '~/components/Builder/Configure/SetThumbnail/SetThumbnailPreview';
import { opacityHex } from '@honeybook/hbui/dist/utils/color.utils';
import { IconProps } from '@honeybook/icons/dist/types/Icon.interface';
import PublishedTemplateIcon from '~/components/Templates/components/FlowTemplates/PublishedTemplateIcon';
import LeadFormCardActionOverlay from '~/components/LeadCaptureScreen/LeadForms/LeadFormCardActionOverlay';
import { useAuthenticatedGlobalContext } from '~/stores/useAuthenticatedGlobalContext';

interface Props {
    thumbnailImage: IFlowThumbnail;
    isFlowCard?: boolean;
    brandColor?: string;
    defaultIconSize: 16 | 42;
    className?: string;
    hasPendingChanges?: boolean;
    isLeadForm?: boolean;
    isInactive?: boolean;
    icon?: (props: IconProps) => JSX.Element;
    iconColor?: string;
    backgroundColor?: string;
    aspectRatio?: number;
    isSharedTemplate?: boolean;
    flow?: IFlowTemplate;
    goToFlow?: () => void;
}

const HEIGHT = 184;

const FlowThumbnail = ({
    thumbnailImage,
    isFlowCard,
    brandColor,
    icon: Icon,
    iconColor,
    backgroundColor,
    defaultIconSize,
    className = '',
    hasPendingChanges,
    isLeadForm,
    isInactive,
    aspectRatio = THUMBNAIL_CROP_RATIO,
    flow,
    goToFlow
}: Props) => {
    const {
        companyStore: { currCompany }
    } = useAuthenticatedGlobalContext();

    const _brandColor = brandColor || currCompany.brand_color;
    const _iconColor = iconColor || _brandColor;
    const _backgroundColor = backgroundColor || opacityHex(_brandColor, 20);

    const thumbnail = isLeadForm
        ? thumbnailImage || currCompany.icon_image
        : thumbnailImage;

    const imageUrl = getOptimizedThumbnailImage(thumbnail, {
        height: HEIGHT,
        width: Math.round(HEIGHT * aspectRatio)
    });

    const _Icon = Icon || FLOW_ICONS[defaultIconSize];

    const showActionsOnHover = isLeadForm && isFlowCard;

    return (
        <FlowThumbnailContainer>
            {flow?.public_configuration?.published_template && (
                <PublishedTemplateIcon flow={flow} />
            )}
            {thumbnail ? (
                <FlowThumbnailImage
                    className={className}
                    imageUrl={imageUrl}
                    isFlowCard={isFlowCard}
                />
            ) : (
                <DefaultThumbnailContainer
                    className={className}
                    $backgroundColor={_backgroundColor}
                    isFlowCard={isFlowCard}
                >
                    <_Icon color={_iconColor} />
                </DefaultThumbnailContainer>
            )}
            {isInactive && <InactiveImageOverlay />}
            {hasPendingChanges && <PendingChangesDotForFlowThumbnail />}
            {showActionsOnHover && (
                <LeadFormCardActionOverlay flow={flow} goToFlow={goToFlow} />
            )}
        </FlowThumbnailContainer>
    );
};
export default observer(FlowThumbnail);

// ------------styles------------

const FLOW_ICONS = {
    16: FileSparks16,
    42: FileSparks42
};

const FlowThumbnailContainer = styled.div`
    width: 100%;
    height: 100%;
    position: relative; // for PendingChangesDotForFlowThumbnail absolute positioning
`;
const FlowCardStyle = css`
    border-radius: 2px 2px 0 0;
    box-shadow: inset 0px 0px ${colors.black_005},
        inset 0px -1px ${colors.black_005};
`;

const FlowThumbnailImage = styled.div<{
    imageUrl: string;
    isFlowCard?: boolean;
}>`
    height: 100%;
    width: 100%;
    background-image: url(${props => props.imageUrl});
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    border-radius: 4px;
    ${is('isFlowCard')`
        ${FlowCardStyle}
    `}
`;

const DefaultThumbnailContainer = styled.div<{
    $backgroundColor?: string;
    isFlowCard?: boolean;
}>`
    ${flexCenterAll}
    width: 100%;
    height: 100%;
    background-color: ${props => props.$backgroundColor};
    border-radius: 4px;
    ${is('isFlowCard')`
        ${FlowCardStyle}
    `}
    i {
        display: flex;
    }
`;

const InactiveImageOverlay = styled.div`
    background: rgba(255, 255, 255, 0.7);
    ${absoluteAll};
`;
